/**
 * @file TransferTargetFields.tsx
 * @description Блок «Куда» формы строки перемещения (phase19.23 этап B).
 *
 * Два режима вместо одного поля: «Ячейка» — перестановка внутри склада
 * (переезжает вся партия, количество не редактируется, W14), «Другой склад» —
 * межскладской переезд (можно увезти часть остатка). Режим — переключатель, а
 * не догадка по заполненному полю: кладовщик должен видеть, какой из двух
 * документов он составляет.
 *
 * Межсклад доступен только администратору склада (`SHOP_WMS_MANAGE`, OQ-46) —
 * без права вариант не показываем, чтобы не ловить 403 после заполнения формы.
 */

import { BinScanField } from "../bins/BinScanField";
import { SelectField } from "../../ui/SelectField";
import { WmsFieldLabel } from "../WmsFieldLabel";
import type { StorageBinDto } from "../../../lib/api-bins";
import type { WarehouseDto } from "../../../lib/api-wms";

/** Куда едет строка: в ячейку своего склада или на другой склад. */
export type TransferTargetMode = "BIN" | "WAREHOUSE";

interface Props {
  mode: TransferTargetMode;
  onModeChange: (mode: TransferTargetMode) => void;
  binId: string;
  onBinChange: (binId: string) => void;
  warehouseId: string;
  onWarehouseChange: (warehouseId: string) => void;
  bins: StorageBinDto[];
  /** Склад-источник: в нём ищется скан ячейки (phase19.22, W14-9). */
  fromWarehouseId?: string;
  targetWarehouses: WarehouseDto[];
  /** false — права на межсклад нет, показываем только ячейку. */
  canMoveBetweenWarehouses: boolean;
  disabled?: boolean;
}

export function TransferTargetFields({
  mode,
  onModeChange,
  binId,
  onBinChange,
  warehouseId,
  onWarehouseChange,
  bins,
  fromWarehouseId,
  targetWarehouses,
  canMoveBetweenWarehouses,
  disabled,
}: Props) {
  return (
    <div className="flex flex-col gap-2">
      {/* Подпись группы — тем же рецептом капса, что у полей (`WmsFieldLabel`):
          свой набор классов давал 10px и вес 700, ниже минимума шкалы. */}
      <span className="label-editorial">Куда</span>

      {canMoveBetweenWarehouses ? (
        <div className="flex flex-col gap-1">
          {/* Радио, а не вкладки: выбор влияет на смысл документа и на то,
              редактируется ли количество. Тач-цель 56px. */}
          {(
            [
              { value: "BIN", label: "Ячейка этого склада" },
              { value: "WAREHOUSE", label: "Другой склад" },
            ] as const
          ).map((option) => (
            <label
              key={option.value}
              className="flex min-h-[48px] cursor-pointer items-center gap-2 text-sm"
            >
              <input
                type="radio"
                name="transfer-target-mode"
                className="size-5"
                checked={mode === option.value}
                onChange={() => onModeChange(option.value)}
                disabled={disabled}
              />
              {option.label}
            </label>
          ))}
        </div>
      ) : null}

      {mode === "BIN" ? (
        // Не `<label>`: у поля ячейки внутри плитки уровня и кнопка сброса.
        <div className="flex flex-col gap-1">
          <span className="label-editorial">Ячейка</span>
          {bins.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Ячеек на складе нет</p>
          ) : (
            <BinScanField
              warehouseId={fromWarehouseId ?? bins[0]?.warehouseId ?? ""}
              bins={bins}
              value={binId}
              onChange={onBinChange}
              disabled={disabled}
            />
          )}
        </div>
      ) : (
        <>
          <WmsFieldLabel label="Склад назначения">
            <SelectField
              value={warehouseId}
              onChange={onWarehouseChange}
              options={targetWarehouses.map((w) => ({ value: w.id, label: w.name }))}
              placeholder="— склад —"
              ariaLabel="Склад назначения"
              disabled={disabled}
            />
          </WmsFieldLabel>
          <p className="text-xs text-on-surface-variant">
            Ячейку на новом складе назначают отдельным документом размещения —
            иначе частичный переезд описать нечем.
          </p>
        </>
      )}
    </div>
  );
}
